import React, { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { createPageUrl } from '@/utils';
import { notify } from '@/components/ui/notify';
import { enrolmentFieldLabel, formatEnrolmentNumber, isPopulationServedMeasure } from '@/lib/enrolmentDisplay';
import { AccountFinal } from '@/api/entities';
import { useNavigate } from 'react-router-dom';
import { cleanQuote, cleanQuotedText, narratesPipeline } from '@/lib/quoteText';
import {
  fetchMarketAccountPreview,
  addMarketAccount,
  ignoreMarketAccount,
  requestAccountEnrichment,
} from '@/lib/marketExperienceApi';
import {
  asList,
  formatConfidence,
  formatCurrency,
  formatRenewalDate,
  formatMomentum,
  formatScoreBreakdown,
  scoreBreakdownParts,
  scorePlainChips,
  activityEvents,
  formatEventWhen,
  formatContractScope,
  formatCategoryLabel,
  getOpportunityTypeMeta,
  getDisplacementGradeMeta,
  getIncumbent,
  isLiveSignalRow,
  isMarketAccountAdded,
  extractAccountPageId,
  extractAddedAccount,
  marketAccountPageId,
  opportunityTypeBadgeClass,
  normalizeMarketAccountCode,
  pickTenantMarketAccountRow,
  rowCategories,
  COLUMN_TOOLTIPS,
  marketAddSuccessTitle,
  ENRICH_TOOLTIP,
  ENRICH_REQUEST_TOAST,
  EVIDENCE_TOOLTIP,
  FIT_ADD_UNSCORED_FALLBACK,
  formatFinancialCapacity,
  resolveOpportunityType,
  mktLabel,
} from '@/lib/marketExperience';
import { useTranslation } from '@/components/utils/translations';
import { fundingBlocksFromRecords, priorFundingEntries, priorFundingYear } from '@/lib/marketFunding';
import MarketHelpLabel, { MarketHelpButton } from './MarketHelpLabel';
import { Lock } from 'lucide-react';
import {
  isNotInPilotError,
  isPilotAtCap,
  isPilotModeOn,
  navigateToPilotLockedPage,
  PILOT_NO_SWAP_TOOLTIP,
  pilotErrorMessage,
  pilotLockChipLabel,
} from '@/lib/pilotMode';

function Slot({ title, children }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-[#5C6B7A] uppercase tracking-wide">{title}</p>
      <div className="text-sm text-[#0A1A2F]">{children}</div>
    </div>
  );
}

function EnrichButton({ queuedAt, busy, onClick, size }) {
  const { t } = useTranslation();
  const requested = Boolean(queuedAt);
  const label = requested
    ? `${t('mktEnrichmentRequested')}${queuedAt ? ` · ${new Date(queuedAt).toLocaleDateString()}` : ''}`
    : t('mktRequestEnrichment');
  const hint = requested
    ? t('mktEnrichmentRequestedOn', { date: new Date(queuedAt).toLocaleDateString() })
    : mktLabel(t, ENRICH_TOOLTIP);
  return (
    <div className="relative w-full">
      <Button variant="outline" className="w-full pr-8" disabled={busy || requested} onClick={onClick}>
        {busy ? t('mktRequesting') : label}
      </Button>
      <div className="absolute right-2 top-1/2 -translate-y-1/2">
        <MarketHelpButton label={t('mktAboutEnrichment')} hint={hint} />
      </div>
    </div>
  );
}

function capitalizeFirst(text) {
  const s = String(text || '');
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function isTruncatedDetail(text) {
  const s = String(text || '').trim();
  return s.endsWith('…') || s.endsWith('...');
}

function isChangeReadinessDriver(driver) {
  const factor = String(driver?.factor || '').toLowerCase();
  const label = String(driver?.label || '').toLowerCase();
  return (
    factor.includes('readiness')
    || factor === 'f4_readiness'
    || label.includes('change readiness')
    || label.includes('ai readiness')
  );
}

function isProductAlignmentDriver(driver) {
  const factor = String(driver?.factor || '').toLowerCase();
  const label = String(driver?.label || '').toLowerCase();
  return (
    factor === 'f2_alignment'
    || factor === 'product_alignment'
    || factor === 'alignment'
    || label.includes('product alignment')
  );
}

/** Prefer the short AI readiness summary — never the long basis_full assessment. */
function resolveDriverDetail(driver, row, payload) {
  const detail = String(driver?.detail || '');
  if (!isChangeReadinessDriver(driver)) return detail;
  const summary = String(
    row?.ai_readiness_summary
    || payload?.ai_readiness_summary
    || payload?.row?.ai_readiness_summary
    || row?.ai_readiness?.basis
    || '',
  ).trim();
  // Some summaries narrate our own research ("The full 1,588-URL sitemap…"); the engine's detail
  // is filtered for that, so prefer it over one of those.
  if (summary && !narratesPipeline(summary)) return summary;
  // Never fall back to the long assessment on the card.
  if (isTruncatedDetail(detail)) return detail.replace(/\s*[….]{1,3}\s*$/, '').trim();
  return detail;
}

const DRIVER_SOURCE_LINK_CLASS = 'text-[11px] text-[#00C2C2] hover:underline';

function isCommercialOrPainDriver(driver) {
  const factor = String(driver?.factor || '').toLowerCase();
  const label = String(driver?.label || '').toLowerCase();
  return (
    factor === 'f5_commercial'
    || factor === 'f1_pain'
    || label.includes('commercial readiness')
    || label.includes('pain severity')
  );
}

function renderDriverSourcesInline(sources) {
  if (!Array.isArray(sources) || !sources.length) return null;
  return sources.map((s, si) => (
    <span key={`${s.url || s.label}-${si}`}>
      {si > 0 ? ' · ' : null}
      {s.url ? (
        <a
          href={s.url}
          target="_blank"
          rel="noopener noreferrer"
          className={DRIVER_SOURCE_LINK_CLASS}
        >
          {s.label}
        </a>
      ) : (
        <span className="text-[11px]">{s.label}</span>
      )}
      {s.dated ? <span className="text-[11px] opacity-70"> ({s.dated})</span> : null}
    </span>
  ));
}

function driverSourceUrl(driver, events = [], detailOverride = null) {
  const first = (driver?.sources || []).find((s) => s?.url);
  if (first) return first.url;
  const direct = driver?.source_url || driver?.url || driver?.evidence_url;
  if (direct) return direct;
  const detail = String(detailOverride || driver?.detail || '');
  if (!detail) return null;
  for (const ev of events) {
    if (!ev?.source_url) continue;
    const quote = String(ev.quote || '');
    const what = String(ev.what || '');
    if (quote && detail.includes(quote.slice(0, Math.min(48, quote.length)))) return ev.source_url;
    if (what && detail.toLowerCase().includes(what.toLowerCase().slice(0, 32))) return ev.source_url;
  }
  return null;
}

function ScoreMetricTooltip({ label, value, means, reason, className = '' }) {
  const text = `${label} ${value}`;
  if (!means) {
    return <span className={className}>{text}</span>;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={`underline decoration-dotted underline-offset-2 cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${className}`}
        >
          {text}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-xs text-xs leading-relaxed">
        <p>{means}</p>
        {reason ? <p className="mt-1.5 opacity-80">{reason}</p> : null}
      </TooltipContent>
    </Tooltip>
  );
}

export default function MarketOpportunityDrawer({
  tenantId,
  lens,
  accountId,
  seedRow,
  open,
  onOpenChange,
  showPricing = true,
  onMovedToIgnore,
  onAccountAdded,
  onPilotUsed,
  pilotStatus = null,
  queue = null,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [enrichQueuedAt, setEnrichQueuedAt] = useState(null);
  const [addedLocal, setAddedLocal] = useState(false);
  const [pageId, setPageId] = useState(null);
  const [fitOverride, setFitOverride] = useState(null);
  const [showOtherEvents, setShowOtherEvents] = useState(false);

  const resolvePageId = async (payload, { propagatePilotLock = false } = {}) => {
    const fromPayload = extractAccountPageId(payload, seedRow);
    if (fromPayload) {
      setPageId(fromPayload);
      return fromPayload;
    }
    const marketId = accountId || seedRow?.account_id;
    const name = seedRow?.account_name || payload?.account_name || payload?.row?.account_name || payload?.account?.account_name;
    if (!tenantId) return null;
    const code = normalizeMarketAccountCode(marketId, tenantId)
      || normalizeMarketAccountCode(seedRow?.tenant_account_id, tenantId);
    const run = (where) => AccountFinal.filter(where, '', 5).catch((e) => {
      if (isNotInPilotError(e)) {
        if (propagatePilotLock) throw e;
        return [];
      }
      return [];
    });
    const queries = [];
    if (code) queries.push(run({ tenant_id: tenantId, account_id: code }));
    if (marketId && marketId !== code) {
      queries.push(run({ tenant_id: tenantId, account_id: marketId }));
    }
    if (name) queries.push(run({ tenant_id: tenantId, account_name: name }));
    const results = await Promise.all(queries);
    const hit = pickTenantMarketAccountRow(results.flat(), {
      tenantId,
      poolAccountId: marketId || seedRow?.account_id,
    });
    const id = hit ? extractAccountPageId(hit) : null;
    if (id) setPageId(id);
    return id;
  };

  useEffect(() => {
    let cancelled = false;
    setShowOtherEvents(false);
    if (!open || !accountId || !tenantId) {
      setPayload(null);
      setEnrichQueuedAt(null);
      setAddedLocal(false);
      setPageId(null);
      setFitOverride(null);
      return;
    }
    setPageId(extractAccountPageId(seedRow) || null);
    setLoading(true);
    fetchMarketAccountPreview(tenantId, accountId, lens)
      .then(async (data) => {
        if (cancelled) return;
        setPayload(data);
        await resolvePageId(data);
      })
      .catch(() => {
        if (!cancelled) setPayload({ row: null });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, accountId, tenantId, lens]);

  const row = payload?.row === null ? null : (payload?.row || payload?.account || payload || seedRow);
  const empty = open && !loading && (payload?.row === null || !row);

  const typeKey = resolveOpportunityType(row);
  const typeMeta = getOpportunityTypeMeta(typeKey);
  const unknowns = asList(row?.unknowns);
  const triggers = asList(row?.triggers);
  const scoreDrivers = asList(row?.drivers || payload?.drivers).filter((d) => (
    d
    && typeof d === 'object'
    && (d.detail || d.label || d.factor || d.points != null)
    && !isProductAlignmentDriver(d)
  ));
  const neoView = row?.neo_view || payload?.neo_view;
  const whyNow = row?.why_now ? cleanQuotedText(row.why_now) : null;
  const fundingItems = fundingBlocksFromRecords(row, payload, seedRow);
  const priorFunding = priorFundingEntries(row, payload, seedRow);
  const fitBlock = fitOverride || payload?.fit || row?.fit;
  const fitStatus = fitBlock?.status;
  const fitScored = fitOverride?.scored === true
    || fitStatus === 'scored'
    || (fitBlock?.score != null && fitBlock.score !== '');
  const fitScore = fitBlock?.score ?? row?.account_fit_score ?? payload?.account_fit_score;
  const fitLabel = fitBlock?.label ?? payload?.account_fit_label ?? row?.account_fit_label;
  const fitTier = fitBlock?.tier ?? row?.account_tier;
  const marketScore = row?.market_score ?? payload?.market_score;
  const evidence = formatConfidence(row?.confidence_factor ?? payload?.confidence_factor ?? row?.score_breakdown?.confidence_factor);
  const breakdownLine = formatScoreBreakdown(row?.score_breakdown, row);
  const scoreParts = scoreBreakdownParts(row?.score_breakdown, row);
  const scoreSentence = row?.score_breakdown?.plain?.sentence;
  const scoreChips = scorePlainChips(row?.score_breakdown, row);
  const activity = row?.activity || payload?.activity;
  const events = activityEvents({ activity });
  // Events outside the lens's categories (another department's contract, a vendor in a product
  // line the tenant does not sell, an uncategorised budget line) and STALE events (aged past the
  // engine's recency cut-off — the lighter rows) are real but not evidence for this deal. Hidden
  // by default so the list reads as the case for the opportunity; one click shows them.
  const isRelevantEvent = (ev) => Boolean(ev.in_lens_category) && !ev.stale;
  const otherEventCount = events.filter((ev) => !isRelevantEvent(ev)).length;
  const shownEvents = showOtherEvents ? events : events.filter(isRelevantEvent);
  const cats = rowCategories(row);
  const incumbent = getIncumbent(row);
  const gradeMeta = getDisplacementGradeMeta(incumbent.displacement_grade);
  const queuedAt = enrichQueuedAt || row?.enrichment_requested_at || row?.enrichment_queued_at;
  const added = addedLocal
    || isMarketAccountAdded(seedRow)
    || isMarketAccountAdded(row, payload);
  const accountPageId = pageId || marketAccountPageId(row) || marketAccountPageId(seedRow);
  const pilotOn = isPilotModeOn(pilotStatus);
  const atCap = isPilotAtCap(pilotStatus);

  const openAccountPage = async () => {
    try {
      if (accountPageId) {
        navigate(createPageUrl(`AccountFinal?id=${encodeURIComponent(accountPageId)}`));
        return;
      }
      const id = await resolvePageId(payload, { propagatePilotLock: true });
      if (id) {
        navigate(createPageUrl(`AccountFinal?id=${encodeURIComponent(id)}`));
        return;
      }
      notify({
        title: t('mktAccountPageNotFound'),
        description: t('mktCouldNotResolveAccount'),
        variant: 'destructive',
      });
    } catch (e) {
      if (isNotInPilotError(e)) {
        navigateToPilotLockedPage(navigate, createPageUrl, e.body || {});
        return;
      }
      notify({
        title: t('mktCouldNotOpenAccount'),
        description: e?.message || t('mktPleaseTryAgain'),
        variant: 'destructive',
      });
    }
  };

  const enrichScope = (() => {
    const list = asList(unknowns)
      .map((u) => (typeof u === 'string' ? u : u?.key || u?.field || u?.scope))
      .filter(Boolean);
    return list.length ? list : ['personas'];
  })();

  const runAction = async (key, fn, success) => {
    setBusy(key);
    try {
      const result = await fn();
      const title = key === 'add' ? mktLabel(t, marketAddSuccessTitle(result)) : success;
      notify({
        title,
        variant: 'success',
        ...(key === 'enrich' ? { description: mktLabel(t, ENRICH_REQUEST_TOAST) } : {}),
      });
      if (key === 'enrich') setEnrichQueuedAt(new Date().toISOString());
      if (key === 'add') {
        setAddedLocal(true);
        onAccountAdded?.(accountId || seedRow?.account_id, { in_accounts: true, already_added: true });
        const added = extractAddedAccount(result);
        if (added) {
          setFitOverride({
            status: added.scored === false ? 'not_yet_scored' : 'scored',
            score: added.account_fit_score,
            tier: added.account_tier,
            label: added.account_fit_label,
            scored: added.scored !== false && added.account_fit_score != null,
            reason: added.scored === false ? FIT_ADD_UNSCORED_FALLBACK : undefined,
          });
        }
        let id = await resolvePageId(result);
        if (!id) {
          await new Promise((r) => setTimeout(r, 400));
          id = await resolvePageId(result);
        }
        if (id) {
          onAccountAdded?.(accountId || seedRow?.account_id, {
            in_accounts: true,
            already_added: true,
            account_record_id: id,
          });
        }
        onPilotUsed?.();
      }
      if (key === 'skip') {
        onMovedToIgnore?.({
          accountId: accountId || seedRow?.account_id,
          accountName: row?.account_name || seedRow?.account_name,
          result,
        });
        onOpenChange?.(false);
      }
      return true;
    } catch (e) {
      if (key === 'add') {
        setAddedLocal(false);
        onAccountAdded?.(accountId || seedRow?.account_id, { in_accounts: false, already_added: false });
      }
      notify({
        title: t('mktCouldNotCompleteAction'),
        description: pilotErrorMessage(e, t('mktPleaseTryAgain')),
        variant: 'destructive',
      });
      return false;
    } finally {
      setBusy(null);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-[#0A1A2F] pr-6">
            {row?.account_name || seedRow?.account_name || t('mktOpportunity')}
          </SheetTitle>
          <SheetDescription>
            {row?.country || seedRow?.country || ''}
            {row?.sector ? ` · ${row.sector}` : ''}
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="mt-6 space-y-3">
            <div className="h-24 bg-gray-100 rounded-md animate-pulse" />
            <div className="h-40 bg-gray-100 rounded-md animate-pulse" />
          </div>
        ) : empty ? (
          <p className="mt-8 text-sm text-[#5C6B7A]">{t('mktNothingToPreview')}</p>
        ) : (
          <div className="mt-6 space-y-6">
            <div className="flex flex-wrap gap-2 items-center">
              <span className={`inline-flex text-[11px] font-medium px-1.5 py-0.5 rounded ${opportunityTypeBadgeClass(typeKey)}`}>
                {mktLabel(t, typeMeta.label)}
              </span>
              {isLiveSignalRow(row) && (
                <Badge className="bg-[#00C2C2] text-white border-0">{t('mktLiveSignal')}</Badge>
              )}
              {gradeMeta && (
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${gradeMeta.className}`} title={mktLabel(t, COLUMN_TOOLTIPS.grade)}>
                  {mktLabel(t, gradeMeta.label)}
                </span>
              )}
            </div>
            {gradeMeta?.key === 'implied' && (
              <p className="text-xs text-[#5C6B7A]">{mktLabel(t, gradeMeta.hint)}</p>
            )}
            {row?.incumbent_sentiment && (
              <p className="text-xs text-[#5C6B7A]">{t('mktIncumbentSentimentLine', { sentiment: row.incumbent_sentiment })}</p>
            )}

            <div>
              <p className="text-[10px] uppercase tracking-wide text-[#5C6B7A]">
                <MarketHelpLabel label={t('mktMarketScore')} hint={mktLabel(t, COLUMN_TOOLTIPS.score)} />
              </p>
              <p className="text-3xl font-semibold text-[#0A1A2F] mt-1">{marketScore ?? '—'}</p>
              {scoreSentence ? (
                <p className="text-xs text-[#5C6B7A] mt-1">{scoreSentence}</p>
              ) : scoreParts.length > 0 ? (
                <TooltipProvider delayDuration={150}>
                  <p className="text-xs text-[#5C6B7A] mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1">
                    {scoreParts.map((p, i) => (
                      <span key={p.key} className="inline-flex items-center gap-1.5">
                        {i > 0 && <span aria-hidden="true" className="opacity-50">·</span>}
                        <ScoreMetricTooltip
                          label={mktLabel(t, p.label)}
                          value={p.value}
                          means={p.means}
                          reason={p.reason}
                        />
                      </span>
                    ))}
                    {marketScore != null && <span className="opacity-70">→ {marketScore}</span>}
                  </p>
                </TooltipProvider>
              ) : breakdownLine ? (
                <p className="text-xs text-[#5C6B7A] mt-1">{breakdownLine}</p>
              ) : null}
              {scoreChips.length > 0 && (
                <TooltipProvider delayDuration={150}>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {scoreChips.map((c) => (
                      <span
                        key={c.key}
                        className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-[#0A1A2F] inline-flex"
                      >
                        <ScoreMetricTooltip
                          label={mktLabel(t, c.label)}
                          value={c.value ?? '—'}
                          means={mktLabel(t, c.means)}
                          reason={c.reason}
                        />
                      </span>
                    ))}
                  </div>
                </TooltipProvider>
              )}
              {(activity?.summary || events.length > 0) && (
                <div className="mt-2 space-y-1.5">
                  {activity?.summary ? (
                    <p className="text-xs text-[#5C6B7A]">{activity.summary}</p>
                  ) : null}
                  {shownEvents.length > 0 && (
                    <ul className="space-y-1.5">
                      {shownEvents.map((ev, i) => {
                        const onTarget = Boolean(ev.about_incumbent && ev.in_lens_category);
                        const when = formatEventWhen(ev.when);
                        const stale = Boolean(ev.stale);
                        const catLabel = ev.category_label || (ev.category ? formatCategoryLabel(ev.category) : '');
                        return (
                          <li
                            key={`${ev.kind || 'ev'}-${i}`}
                            className={`text-xs rounded-md px-2 py-1.5 ${
                              stale
                                ? 'bg-gray-50/70 text-[#94A3B8]'
                                : onTarget
                                  ? 'bg-[#E6F8F8] text-[#0A1A2F] border border-[#00C2C2]/30'
                                  : 'bg-gray-50 text-[#5C6B7A]'
                            }`}
                          >
                            <p>
                              {when ? `${when} · ` : ''}
                              {ev.what || ev.kind}
                              {ev.vendor ? ` · ${ev.vendor}` : ''}
                              {catLabel ? ` · ${catLabel}` : ''}
                              {stale ? ` · ${t('mktStale')}` : ''}
                            </p>
                            {cleanQuote(ev.quote) ? <p className="italic mt-0.5">“{cleanQuote(ev.quote)}”</p> : null}
                            {ev.source_url ? (
                              <a href={ev.source_url} target="_blank" rel="noreferrer" className="text-[#00C2C2] hover:underline">{t('source')}</a>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {otherEventCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowOtherEvents((v) => !v)}
                      className="text-xs text-[#00C2C2] hover:underline"
                    >
                      {showOtherEvents
                        ? t('mktHideOlderEvents')
                        : (otherEventCount === 1
                          ? t('mktShowOneOlderEvent', { n: otherEventCount })
                          : t('mktShowNOlderEvents', { n: otherEventCount }))}
                    </button>
                  )}
                </div>
              )}
            </div>

            {fitScored ? (
              <div>
                <p className="text-[10px] uppercase tracking-wide text-[#5C6B7A]">{t('analyticsAccountFit')}</p>
                <div className="mt-1 space-y-1">
                  <p className="text-sm font-medium text-[#0A1A2F]">
                    {fitLabel || `${t('fit')} ${fitScore}`}
                    {fitTier ? ` · ${fitTier}` : ''}
                    {fitScore != null ? ` · ${fitScore}` : ''}
                  </p>
                  {(fitBlock?.rationale || row?.score_rationale) ? (
                    <p className="text-xs text-[#5C6B7A]">{fitBlock?.rationale || row?.score_rationale}</p>
                  ) : null}
                </div>
              </div>
            ) : null}

            {scoreDrivers.length > 0 && (
              <div>
                <p className="text-xs font-medium text-[#5C6B7A] uppercase tracking-wide mb-2">{t('mktWhyThisScore')}</p>
                <ul className="space-y-3">
                  {scoreDrivers.map((d, i) => {
                    const detail = resolveDriverDetail(d, row, payload);
                    const driverSources = Array.isArray(d.sources) ? d.sources : [];
                    const source = driverSources.length ? null : driverSourceUrl(d, events, detail);
                    const fullAssessment = isChangeReadinessDriver(d)
                      ? String(
                        row?.ai_readiness_basis_full
                        || payload?.row?.ai_readiness_basis_full
                        || payload?.ai_readiness_basis_full
                        || '',
                      ).trim()
                      : '';
                    return (
                      <li key={d.factor || i} className="text-sm">
                        <p className="text-[#0A1A2F] font-medium">{d.label || d.factor}</p>
                        {detail ? (
                          <p className="text-[13px] text-[#5C6B7A] mt-0.5 leading-relaxed break-words whitespace-normal">
                            {capitalizeFirst(cleanQuotedText(detail))}
                            {driverSources.length > 0 && isCommercialOrPainDriver(d) ? (
                              <> {renderDriverSourcesInline(driverSources)}</>
                            ) : null}
                            {!driverSources.length && source ? (
                              <>
                                {' '}
                                <a
                                  href={source}
                                  target="_blank"
                                  rel="noreferrer"
                                  className={`${DRIVER_SOURCE_LINK_CLASS} whitespace-nowrap`}
                                >
                                  {t('source')}
                                </a>
                              </>
                            ) : null}
                          </p>
                        ) : null}
                        {driverSources.length > 0 && !isCommercialOrPainDriver(d) && (
                          <ul className="mt-1.5 space-y-0.5">
                            {driverSources.map((s, si) => (
                              <li key={`${s.url || s.label}-${si}`} className="text-[11px] text-[#5C6B7A] flex gap-1.5">
                                <span aria-hidden="true">·</span>
                                {s.url ? (
                                  <a
                                    href={s.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline underline-offset-2 hover:text-[#0A1A2F]"
                                  >
                                    {s.label}
                                  </a>
                                ) : (
                                  <span>{s.label}</span>
                                )}
                                {s.dated ? <span className="opacity-70">({s.dated})</span> : null}
                              </li>
                            ))}
                          </ul>
                        )}
                        {fullAssessment && fullAssessment !== detail ? (
                          <details className="mt-1">
                            <summary className="text-[11px] text-[#00C2C2] cursor-pointer">{t('mktFullAssessment')}</summary>
                            <p className="text-[12px] text-[#5C6B7A] mt-1 leading-relaxed break-words whitespace-normal">
                              {fullAssessment}
                            </p>
                          </details>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {row?.funding_eligibility ? (
              <div>
                <p className="text-[10px] uppercase text-[#5C6B7A]">{t('mktFundingEligibility')}</p>
                <p className="text-sm text-[#0A1A2F] font-medium">
                  {row.funding_eligibility.verdict === 'yes' ? t('mktYes') : t('mktNo')}
                </p>
                {row.funding_eligibility.detail ? (
                  <p className="text-[13px] text-[#5C6B7A] mt-0.5 leading-relaxed break-words whitespace-normal">
                    {row.funding_eligibility.detail}
                    {row.funding_eligibility.call?.source_url ? (
                      <>
                        {' '}
                        <a
                          href={row.funding_eligibility.call.source_url}
                          target="_blank"
                          rel="noreferrer"
                          className={`${DRIVER_SOURCE_LINK_CLASS} whitespace-nowrap`}
                        >
                          {t('source')}
                        </a>
                      </>
                    ) : null}
                  </p>
                ) : null}
              </div>
            ) : null}

            {evidence && (
              <div>
                <p className="text-[10px] uppercase text-[#5C6B7A]">
                  <MarketHelpLabel label={t('mktEvidence')} hint={mktLabel(t, EVIDENCE_TOOLTIP)} />
                </p>
                <p className="text-sm">{mktLabel(t, evidence)}</p>
              </div>
            )}

            {cats.length > 0 && (
              <div>
                <p className="text-xs font-medium text-[#5C6B7A] uppercase tracking-wide mb-2">{t('productFitCategories')}</p>
                <ul className="space-y-2">
                  {cats.map((c) => (
                    <li key={c.category} className="rounded-md border border-gray-100 px-3 py-2 text-sm">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-[#0A1A2F]">{formatCategoryLabel(c.category)}</span>
                        <span className={`inline-flex shrink-0 text-[11px] font-medium px-1.5 py-0.5 rounded ${opportunityTypeBadgeClass(c.opportunity_type)}`}>
                          {mktLabel(t, getOpportunityTypeMeta(c.opportunity_type).label)}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-[#0A1A2F]">
                        {c.incumbent_vendor || '—'}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-[10px] uppercase text-[#5C6B7A]">{enrolmentFieldLabel(row?.enrolment_measure)}</p>
                <p>{formatEnrolmentNumber(row?.enrolment)}</p>
                {row?.enrolment_measure
                  && row.enrolment_measure !== 'total_student_enrolment'
                  && !isPopulationServedMeasure(row.enrolment_measure) && (
                  <p className="text-[11px] text-[#5C6B7A]">{t('mktSubset', { measure: row.enrolment_measure.replace(/_/g, ' ') })}</p>
                )}
              </div>
              <div>
                <p className="text-[10px] uppercase text-[#5C6B7A]">
                  {queue === 'adjacent' ? t('mktAdjacentPlayer') : t('mktIncumbent')}
                </p>
                <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                  <p className="text-sm text-[#0A1A2F]">{incumbent.vendor || '—'}</p>
                  {incumbent.category ? (
                    <span className="text-[11px] text-[#5C6B7A]">
                      {formatCategoryLabel(incumbent.category)}
                    </span>
                  ) : null}
                  {incumbent.state_label ? (
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-[#5C6B7A]">
                      {mktLabel(t, incumbent.state_label)}
                    </span>
                  ) : null}
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase text-[#5C6B7A]">
                  <MarketHelpLabel label={t('mktFinancialCapacity')} hint={mktLabel(t, COLUMN_TOOLTIPS.financial)} />
                </p>
                <p>{mktLabel(t, formatFinancialCapacity(row))}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase text-[#5C6B7A]">{t('mktRenewalDate')}</p>
                <p className="text-sm text-[#5C6B7A]">{formatRenewalDate(row?.renewal_window) || '—'}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase text-[#5C6B7A]">
                  <MarketHelpLabel label={t('momentum')} hint={mktLabel(t, COLUMN_TOOLTIPS.momentum)} />
                </p>
                <p>{formatMomentum(row?.momentum_4w)} / {formatMomentum(row?.momentum_12w)}</p>
              </div>
            </div>

            {showPricing && (
            <div>
              <p className="text-[10px] uppercase text-[#5C6B7A]">{t('mktEstimatedValue')}</p>
              <p className="text-sm">
                {formatCurrency(row?.estimated_value, row?.value_currency) || '—'}
                {row?.estimated_tcv != null && (
                  <span className="text-[#5C6B7A]"> · TCV {formatCurrency(row.estimated_tcv, row.value_currency)}</span>
                )}
              </p>
              {row?.value_provisional && (
                <p className="text-xs text-[#5C6B7A] mt-1">
                  {t('mktProvisional')}{row.value_provisional_reason ? `: ${row.value_provisional_reason}` : ''}
                </p>
              )}
            </div>
            )}

            {(() => {
              const scope = formatContractScope(incumbent.contract_scope);
              return (
                <div>
                  <p className="text-[10px] uppercase text-[#5C6B7A]">
                    <MarketHelpLabel label={t('mktContractScope')} hint={mktLabel(t, scope.hint)} />
                  </p>
                  <p className="text-sm">{scope.display}</p>
                </div>
              );
            })()}

            {whyNow ? <Slot title={t('mktWhyNow')}>{whyNow}</Slot> : null}
            {fundingItems.length > 0 ? (
              <Slot title={t('mktFunding')}>
                <div className="space-y-4">
                  {fundingItems.map((item, i) => (
                    <div key={`${item.programme || 'funding'}-${i}`} className="space-y-2">
                      {item.programme ? (
                        <div>
                          <p className="text-[10px] uppercase text-[#5C6B7A]">{t('mktProgramme')}</p>
                          <p className="text-sm">
                            {item.source_url ? (
                              <a
                                href={item.source_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-[#14B8A6] hover:underline"
                              >
                                {item.programme}
                              </a>
                            ) : item.programme}
                          </p>
                        </div>
                      ) : null}
                      {item.amount ? (
                        <div>
                          <p className="text-[10px] uppercase text-[#5C6B7A]">{t('mktAmount')}</p>
                          <p className="text-sm whitespace-pre-wrap">{item.amount}</p>
                        </div>
                      ) : null}
                      {item.deadline ? (
                        <div>
                          <p className="text-[10px] uppercase text-[#5C6B7A]">{t('deadline')}</p>
                          <p className="text-sm">{item.deadline}</p>
                        </div>
                      ) : null}
                      {item.basis ? (
                        <div>
                          <p className="text-[10px] uppercase text-[#5C6B7A]">{t('mktWhyTheyQualify')}</p>
                          <p className="text-sm whitespace-pre-wrap">{item.basis}</p>
                        </div>
                      ) : null}
                      {item.eligible_spend ? (
                        <div>
                          <p className="text-[10px] uppercase text-[#5C6B7A]">{t('mktEligibleSpend')}</p>
                          <p className="text-sm whitespace-pre-wrap">“{item.eligible_spend}”</p>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </Slot>
            ) : null}
            {priorFunding.length > 0 ? (
              <Slot title={t('mktPreviouslyFunded')}>
                <div className="space-y-4">
                  {priorFunding.map((item, i) => {
                    const year = priorFundingYear(item.document_date);
                    const heading = [item.programme_name, item.funder].filter(Boolean).join(' · ');
                    const quote = cleanQuote(item.evidence_quote);
                    return (
                      <div key={`${item.funder || 'prior'}-${i}`} className="space-y-1">
                        {heading || year ? (
                          <p className="text-sm font-medium">
                            {heading}
                            {year ? `${heading ? ' · ' : ''}${year}` : ''}
                          </p>
                        ) : null}
                        {quote ? (
                          item.source_url ? (
                            <a
                              href={item.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block text-sm whitespace-pre-wrap hover:text-[#14B8A6] hover:underline"
                            >
                              “{quote}”
                            </a>
                          ) : (
                            <p className="text-sm whitespace-pre-wrap">“{quote}”</p>
                          )
                        ) : null}
                        {item.category_relevance ? (
                          <p className="text-xs text-[#5C6B7A]">{item.category_relevance}</p>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </Slot>
            ) : null}
            {triggers.length > 0 && (
              <Slot title={t('mktTriggers')}>
                <ul className="list-disc pl-4">
                  {triggers.map((trigger, i) => (
                    <li key={i}>{typeof trigger === 'string' ? trigger.replace(/_/g, ' ') : trigger.label || trigger}</li>
                  ))}
                </ul>
              </Slot>
            )}
            {neoView ? (
              <Slot title={t('mktNeoView')}>{typeof neoView === 'string' ? neoView : JSON.stringify(neoView)}</Slot>
            ) : null}

            <div className="flex flex-col gap-2 pt-2">
              {pilotOn && atCap && !added ? (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center justify-center gap-1.5 h-9 px-3 text-xs font-medium rounded-md bg-gray-100 text-[#5C6B7A] border border-dashed border-gray-300">
                        <Lock className="w-3.5 h-3.5" />
                        {pilotLockChipLabel(pilotStatus)}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{PILOT_NO_SWAP_TOOLTIP}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : !added ? (
                <Button
                  className="bg-[#00C2C2] hover:bg-[#00A8A8] text-white"
                  disabled={busy === 'add'}
                  onClick={() => {
                    setAddedLocal(true);
                    onAccountAdded?.(accountId || seedRow?.account_id, { in_accounts: true, already_added: true });
                    runAction('add', () => addMarketAccount(tenantId, { account_id: accountId }, lens), t('mktAddedToAccounts'));
                  }}
                >
                  {pilotOn ? t('mktAddToPilot') : t('mktAddToAccounts')}
                </Button>
              ) : (
                <Button
                  className="bg-[#E6F8F8] text-[#0A1A2F] hover:bg-[#D4F1F1] border border-[#00C2C2]/30"
                  onClick={openAccountPage}
                >
                  {pilotOn ? t('mktInPilot') : t('mktSelectedAccount')}
                </Button>
              )}
              {added ? (
                <EnrichButton
                  queuedAt={queuedAt}
                  busy={busy === 'enrich'}
                  onClick={() => runAction(
                    'enrich',
                    () => requestAccountEnrichment(tenantId, accountId, { scope: enrichScope }, lens),
                    t('mktEnrichmentRequested'),
                  )}
                />
              ) : null}
              <Button
                className="bg-[#E6F8F8] text-[#0A1A2F] hover:bg-[#D4F1F1] border border-[#00C2C2]/30"
                disabled={busy === 'skip'}
                onClick={() => runAction('skip', () => ignoreMarketAccount(tenantId, accountId, {}, lens), t('mktMovedToIgnore'))}
              >
                {t('mktIgnoreAccount')}
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
